import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import "@abinitio/kendo.all";
export const origKendoGuid = kendo.guid;

// CAT-769
// Valid uuid: string of length 36 (including 4 hyphens), and
// digit 12 (as pos 14 due to hyphens) must be '4' to denote random-based uuid
// More detail: https://tools.ietf.org/html/rfc4122
//
// This impl has format:
// ${session_id_16}-${secondaryIndex_4}-${baseIndex_12}
// Client Id: First RANDOM 16 digits (base 16) = 15**16 (digit 12 is '4' denoting random UUID) = 1.2e+18
// So chances of two browser session having same generated session id (1 to million million million) is minuscule.
//
// secondaryIndex (base 16, length 4) is incremented whenever baseIndex (base 10) rolls over at 999999999999.  However
// the first byte according to the rfc (and kendo.guid implementation) is restricted to being between [8, B].
// Thus 4*16*16*16 possible values (2^14 = 16k = 1.6e+4)
//
// baseIndex (base 10, length 12) is incremented for every guid generation.  There are 1e+12 possible values.
// It is base 10 as experiments showed that base 16 conversion had significant overhead.
// (Took 43 ms to generate 500,000 guids base 10, and took 113ms if base 16).
//
// What are the chances of running out of instance ids in a given browser session?
// So there are 1.6e+4 * 1e+12 = 1.6e+16 instance ids generated before a given session session runs out of instance ids
// So if generate 10 million guids (1e+7) a second, would run out in 1.6e+9 seconds, which is 50.7 years
// So it won't happen until after I retire ;-)
// Note that if a browser doesn't run for 50 years, then there is 0% chance of internally colliding with an already
// generated UUID within the current browser session; Note that this is a stronger guarantee than the
// totally random-based kendo.guid implementation (and this is the more relevant use case, as a code analysis shows
// that there are NO generated guids that are currently being serialized to the server where they could 'collide'
// with generated guids from other sessions).
//
// Note that javascript number max int is 2^53 = 9e+15, so dividing into secondary and base index numbers is necessary
// to prevent overflow (secondary has two bytes (16 bits) of precision, and baseIndex has 6 bytes (48-bits) respectively).
// Split at hyphen boundary and pad with zeroes to reduce string concatenation logic and overhead.

/**
 * Only exported for testing; please use exported 'guid()' function instead
 * @deprecated
 */
export class RolloverGuid {
  static buildGuidGenerator() {
    return RolloverGuid.buildGuidGeneratorForTesting();
  }

  // Use <any> in tests to access this method for tests
  static buildGuidGeneratorForTesting(baseIndex = 0, secondaryIndex = 0x8000, sessionGuid = RolloverGuid.genClientGuid(16)) {
    return new RolloverGuid(baseIndex, secondaryIndex, sessionGuid);
  }

  // Basically a re-implementation of kendo.guid where number of digits is parameterized.  Very inefficient as
  // requires 32 random() invocations per generated guid with associated string concatenation per iteration.
  // We're only using it to generate a 'random' 16 digit handle for current session.
  static genClientGuid(numDigits = 32) {
    let id = "";
    let random;
    for (let i = 0; i < numDigits; i++) {
      random = Math.random() * 16 | 0;
      if (i === 8 || i === 12 || i === 16 || i === 20) {
        id += "-";
      }
      id += (i === 12 ? 4 : i === 16 ? random & 3 | 8 : random).toString(16);
      // random & 3 means only the last two bits (6, 7) count of second byte count
      // and '| 8' means bit 4 of second byte is always 1
      // so   000010XX  -> 8-11 base 10, which is 08, 09, 0A, 0B  --> if ever reach 0C you blew it!
    }
    return id;
  }

  ///////////////

  // recalculated every time secondary index changes

  constructor(baseIndex, secondaryIndex, sessionGuid) {
    _defineProperty(this, "paddedSessionGuid", void 0);
    _defineProperty(this, "baseGuidPrefix", void 0);
    this.baseIndex = baseIndex;
    this.secondaryIndex = secondaryIndex;
    if (sessionGuid.length !== 18) {
      // defensive check for length 18 (includes two hyphens):  51240073-1301-4903
      throw new Error("Session guid must be 18 chars (including two hyphens), see https://tools.ietf.org/html/rfc4122");
    }
    this.paddedSessionGuid = sessionGuid + "-0000";
    this.baseGuidPrefix = this.incBaseGuidPrefix();
    this.baseIndex = baseIndex;
    this.guid = this.guid.bind(this);
  }
  guid() {
    const guidStr = String(this.baseIndex++);
    const guidLen = guidStr.length;
    if (guidLen > 12) {
      this.baseGuidPrefix = this.incBaseGuidPrefix();
      return this.guid();
    }
    // 36 length includes 4 '-' separators
    return this.baseGuidPrefix.substr(0, 36 - guidStr.length) + guidStr;
  }

  // increments secondary index and resets base index to 0, then recalcs paddedSessionGuid
  incBaseGuidPrefix() {
    if (this.secondaryIndex < 0x8000 || this.secondaryIndex >= 0xC000) {
      // Intentional: be very obvious, as don't want a case where we get subtle errors due to non-unique guids!
      // See comment in  genClientGuid for byte 16 (must be in range of [0x8-0xB] to be consistent w/kendo.guid impl)
      throw new Error("Ran out of unique guids in kendo.grid polyfill!");
    }
    const secondaryGuidStr = (this.secondaryIndex++).toString(16);
    this.baseIndex = 0;
    const secondaryOffset = this.paddedSessionGuid.length - secondaryGuidStr.length;
    return this.paddedSessionGuid.substr(0, secondaryOffset) + secondaryGuidStr + "-000000000000";
  }
}

// guid method already bound to be a function in RolloverGuid construtor
const defaultRolloverGuidFn = () => RolloverGuid.buildGuidGenerator().guid();
export let guid = defaultRolloverGuidFn;

/*
  They are not really globally unique, so probably should be in a separate file from GuidUtils?

let clientGuid: number = 1;

/!** String guaranteed to be unique for this client session *!/
export function clientGuidStr(): string {
  return String(clientGuid++);
}

/!** Number guaranteed to be unique for this client session *!/
export function clientGuidNum(): number {
  return clientGuid++;
}
*/
//# sourceMappingURL=GuidUtils.js.map
